import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { track } from '../lib/tracker';

/**
 * Single newsletter signup component used everywhere — homepage hero, footer,
 * weekend-strip, post-vote card, itinerary email-me, etc. Variants:
 *   - `inline` (default): pale background, single line on desktop
 *   - `dark`: charcoal background, e.g. homepage final CTA
 *   - `compact`: small, no headline (for embedding next to other content)
 *
 * `showCompetition` opts the form into the quarterly prize draw. Default is
 * TRUE for headline placements (footer/homepage) and FALSE for contextual
 * forms (e.g. post-vote) where the prize is a separate value-add.
 */
export default function NewsletterSignup({
  variant = 'inline',
  source = 'homepage',
  showCompetition = true,
  buttonLabel = null,
  successMessage = null,
}) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  // Tick by default — visitors are highly likely to want a free £169+ prize.
  // They can untick if they don't.
  const [enterCompetition, setEnterCompetition] = useState(true);

  async function submit(e) {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      await api.post('/newsletter', {
        email,
        source,
        enter_competition: showCompetition ? enterCompetition : false,
      });
      track.newsletterSignup(source);
      setStatus('done');
      setEmail('');
    } catch (err) {
      setStatus('error');
    }
  }

  const submitLabel = buttonLabel || (variant === 'dark' ? 'Get the weekend digest' : 'Sign me up');
  const doneLabel = successMessage || (showCompetition ? "You're in! Check your inbox 🎉" : "You're in!");

  // --- shared bits ---
  const competitionFootnote = showCompetition && (
    <label className="flex items-start gap-2 text-xs mt-3 cursor-pointer" data-testid="newsletter-competition-label">
      <input
        type="checkbox"
        checked={enterCompetition}
        onChange={(e) => setEnterCompetition(e.target.checked)}
        className="mt-0.5 cursor-pointer accent-mustard"
        data-testid="newsletter-competition-checkbox"
      />
      <span className={variant === 'dark' ? 'text-base/70' : 'text-charcoal/60'}>
        Enter me into the <Link to="/competition" className="underline hover:text-mustard">quarterly prize draw</Link>{' '}
        for a StayPlayExplore voucher (UK, 18+, T&amp;Cs apply).
      </span>
    </label>
  );

  if (variant === 'dark') {
    return (
      <div data-testid="newsletter-form-wrapper">
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
          <input
            type="email" required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="flex-1 bg-white/10 border border-white/20 rounded-full px-6 py-4 text-base placeholder-base/50 focus:outline-none focus:border-mustard"
            data-testid="newsletter-input"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="bg-mustard text-charcoal font-semibold px-8 py-4 rounded-full hover:bg-mustard/90 transition-all hover:scale-[1.03] disabled:opacity-50"
            data-testid="newsletter-submit"
          >
            {status === 'done' ? doneLabel : status === 'loading' ? '...' : submitLabel}
          </button>
        </form>
        {competitionFootnote}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div data-testid="newsletter-form-wrapper">
        <form onSubmit={submit} className="flex gap-2" data-testid="newsletter-form">
          <input
            type="email" required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="flex-1 bg-white border border-charcoal/15 rounded-full px-4 py-2.5 text-sm placeholder-charcoal/40 focus:outline-none focus:border-forest"
            data-testid="newsletter-input"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="bg-heritage text-base font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-heritage-deep transition-all disabled:opacity-50"
            data-testid="newsletter-submit"
          >
            {status === 'done' ? '✓' : status === 'loading' ? '...' : submitLabel}
          </button>
        </form>
        {competitionFootnote}
      </div>
    );
  }

  return (
    <div data-testid="newsletter-form-wrapper">
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
        <input
          type="email" required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="flex-1 bg-white border border-charcoal/15 rounded-full px-6 py-4 placeholder-charcoal/40 focus:outline-none focus:border-forest"
          data-testid="newsletter-input"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="bg-heritage text-base font-semibold px-8 py-4 rounded-full hover:bg-heritage-deep transition-all hover:scale-[1.03] disabled:opacity-50"
          data-testid="newsletter-submit"
        >
          {status === 'done' ? doneLabel : status === 'loading' ? '...' : submitLabel}
        </button>
      </form>
      {competitionFootnote}
    </div>
  );
}
